import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  TrendingUp,
  TrendingDown,
  BarChart3,
  Calendar,
  Award,
  Crown,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  User,
  Users,
  Star,
  CalendarDays,
  Package,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const COLORS = ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

const PerformancePage = () => {
  const { user, api, isAdmin, isManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [viewMode, setViewMode] = useState('team');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [monthDetails, setMonthDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);

  const isManagerOrAdmin = isAdmin() || isManager();

  // Fetch team data
  useEffect(() => {
    const fetchTeamData = async () => {
      if (!isManagerOrAdmin) return;
      try {
        const response = await api.get('/performance/team');
        setTeamData(response.data);
        const currentMonth = response.data.monthly_data.find(m => m.is_current);
        if (currentMonth) {
          setSelectedMonth(currentMonth);
        } else if (response.data.monthly_data.length > 0) {
          setSelectedMonth(response.data.monthly_data[response.data.monthly_data.length - 1]);
        }
      } catch (error) {
        console.error('Failed to fetch team data:', error);
      }
    };
    if (isManagerOrAdmin) {
      fetchTeamData();
    }
  }, [api, isManagerOrAdmin]);

  // Fetch individual performance data
  useEffect(() => {
    const fetchPerformance = async () => {
      const userId = isManagerOrAdmin && viewMode === 'individual' && selectedUserId 
        ? selectedUserId 
        : user?.id;
      
      if (!userId) return;
      if (isManagerOrAdmin && viewMode === 'team') {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const response = await api.get(`/performance/executive/${userId}`);
        setPerformanceData(response.data);
        const currentMonth = response.data.monthly_data.find(m => m.is_current);
        if (currentMonth) {
          setSelectedMonth(currentMonth);
        } else if (response.data.monthly_data.length > 0) {
          setSelectedMonth(response.data.monthly_data[response.data.monthly_data.length - 1]);
        }
      } catch (error) {
        console.error('Failed to fetch performance:', error);
        toast.error('Failed to load performance data');
      } finally {
        setLoading(false);
      }
    };
    
    if (!isManagerOrAdmin || viewMode === 'individual') {
      fetchPerformance();
    } else {
      setLoading(false);
    }
  }, [api, user, selectedUserId, viewMode, isManagerOrAdmin]);

  // Fetch month details when month changes
  const fetchMonthDetails = useCallback(async (month) => {
    if (!month) return;
    
    setLoadingDetails(true);
    try {
      const showTeamView = isManagerOrAdmin && viewMode === 'team';
      const userId = isManagerOrAdmin && viewMode === 'individual' && selectedUserId 
        ? selectedUserId 
        : user?.id;
      
      let url;
      if (showTeamView) {
        url = `/performance/team/month/${month.year}/${month.month_num}`;
      } else {
        url = `/performance/executive/${userId}/month/${month.year}/${month.month_num}`;
      }
      
      const response = await api.get(url);
      setMonthDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch month details:', error);
    } finally {
      setLoadingDetails(false);
    }
  }, [api, isManagerOrAdmin, viewMode, selectedUserId, user]);

  useEffect(() => {
    if (selectedMonth) {
      fetchMonthDetails(selectedMonth);
    }
  }, [selectedMonth, fetchMonthDetails]);

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
          <p className="text-slate-900 font-semibold text-sm mb-1">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-slate-500">{entry.name}:</span>
              <span className="text-slate-900 font-semibold">{entry.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center mx-auto mb-4">
            <RefreshCw className="w-6 h-6 text-white animate-spin" />
          </div>
          <p className="text-slate-500 font-medium">Loading performance data...</p>
        </div>
      </div>
    );
  }

  const showTeamView = isManagerOrAdmin && viewMode === 'team' && teamData;
  const dataToShow = showTeamView ? teamData : performanceData;
  
  if (!dataToShow) {
    return (
      <div className="text-center py-20">
        <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">No Performance Data</h2>
        <p className="text-slate-500">Performance data will appear once sales are recorded.</p>
      </div>
    );
  }

  const { summary, monthly_data } = dataToShow;
  const chartData = monthly_data.map(m => ({
    month: m.month.split(' ')[0],
    sales: m.total_sales,
    average: m.daily_average
  }));

  return (
    <div className="space-y-6" data-testid="performance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Performance Analytics</h1>
          <p className="text-slate-500 text-sm mt-1">
            {showTeamView ? 'Team-wide performance overview' : 'Individual performance breakdown'}
          </p>
        </div>
        
        {isManagerOrAdmin && (
          <div className="flex items-center gap-3">
            <div className="flex bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => { setViewMode('team'); setSelectedMonth(teamData?.monthly_data?.find(m => m.is_current) || teamData?.monthly_data?.slice(-1)[0]); }}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'team'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Users className="w-4 h-4 inline mr-2" />Team Stats
              </button>
              <button
                onClick={() => { setViewMode('individual'); setSelectedUserId(null); }}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'individual'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <User className="w-4 h-4 inline mr-2" />Individual
              </button>
            </div>
            
            {viewMode === 'individual' && teamData?.executives && (
              <Select value={selectedUserId || ''} onValueChange={setSelectedUserId}>
                <SelectTrigger className="w-[200px] bg-white border-slate-200">
                  <SelectValue placeholder="Select executive" />
                </SelectTrigger>
                <SelectContent>
                  {teamData.executives.map(exec => (
                    <SelectItem key={exec.id} value={exec.id}>{exec.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}
      </div>

      {/* Profile Card */}
      <div className={`rounded-2xl p-6 text-white shadow-lg ${
        showTeamView 
          ? 'bg-gradient-to-r from-emerald-600 to-teal-600 shadow-emerald-600/20'
          : 'bg-gradient-to-r from-indigo-600 to-purple-600 shadow-indigo-600/20'
      }`}>
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${showTeamView ? 'bg-white/20' : ''}`}>
            {showTeamView ? (
              <Users className="w-8 h-8 text-white" />
            ) : (
              <Avatar className="w-16 h-16 ring-4 ring-white/20">
                <AvatarFallback className="bg-white/20 text-white text-xl font-bold">
                  {getInitials(performanceData?.user?.name)}
                </AvatarFallback>
              </Avatar>
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold">{showTeamView ? 'Team Performance' : performanceData?.user?.name}</h2>
            <p className={showTeamView ? 'text-emerald-200' : 'text-indigo-200'}>
              {showTeamView ? `${teamData.team_size} Team Members` : performanceData?.user?.email}
            </p>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold">{summary.total_sales}</p>
              <p className="text-sm opacity-80">Total Sales</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold">{summary.average_monthly}</p>
              <p className="text-sm opacity-80">Avg/Month</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold">{summary.months_active}</p>
              <p className="text-sm opacity-80">Months Active</p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-4 mt-6 md:hidden">
          <div className="text-center bg-white/10 rounded-xl p-3">
            <p className="text-2xl font-bold">{summary.total_sales}</p>
            <p className="text-xs opacity-80">Total Sales</p>
          </div>
          <div className="text-center bg-white/10 rounded-xl p-3">
            <p className="text-2xl font-bold">{summary.average_monthly}</p>
            <p className="text-xs opacity-80">Avg/Month</p>
          </div>
          <div className="text-center bg-white/10 rounded-xl p-3">
            <p className="text-2xl font-bold">{summary.months_active}</p>
            <p className="text-xs opacity-80">Months Active</p>
          </div>
        </div>
        
        {summary.best_month && (
          <div className="mt-4 flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2 w-fit">
            <Crown className="w-5 h-5 text-amber-300" />
            <span className="text-sm">Best Month: <strong>{summary.best_month}</strong> with {summary.best_month_sales} sales</span>
          </div>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales Trend Chart */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-5 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              {showTeamView ? 'Team Sales Trend' : 'Sales Trend'}
            </h3>
            <p className="text-sm text-slate-500 mt-1">Monthly sales performance</p>
          </div>
          <div className="p-5">
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorSalesPerf" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={showTeamView ? '#10b981' : '#6366f1'} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={showTeamView ? '#10b981' : '#6366f1'} stopOpacity={0.02}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="sales"
                    name="Sales"
                    fill="url(#colorSalesPerf)"
                    stroke={showTeamView ? '#10b981' : '#6366f1'}
                    strokeWidth={2}
                    dot={{ fill: showTeamView ? '#10b981' : '#6366f1', strokeWidth: 2, r: 4, stroke: '#fff' }}
                    activeDot={{ r: 6, stroke: showTeamView ? '#10b981' : '#6366f1', strokeWidth: 2, fill: '#fff' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Month Selector */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className={`p-5 border-b border-slate-200 ${
            showTeamView 
              ? 'bg-gradient-to-r from-emerald-50 to-teal-50'
              : 'bg-gradient-to-r from-indigo-50 to-purple-50'
          }`}>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Calendar className="w-5 h-5" />Select Month
            </h3>
          </div>
          <div className="p-3 max-h-[300px] overflow-y-auto">
            {monthly_data.slice().reverse().map((month, index) => (
              <button
                key={`${month.year}-${month.month_num}-${index}`}
                onClick={() => setSelectedMonth(month)}
                className={`w-full text-left p-3 rounded-lg mb-2 transition-all ${
                  selectedMonth?.month === month.month && selectedMonth?.year === month.year
                    ? showTeamView 
                      ? 'bg-emerald-50 border border-emerald-200 ring-2 ring-emerald-500/20'
                      : 'bg-indigo-50 border border-indigo-200 ring-2 ring-indigo-500/20'
                    : 'hover:bg-slate-50 border border-transparent'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-slate-900">{month.month}</span>
                    {month.period && <span className="text-xs text-slate-400 ml-2">({month.period})</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-slate-900">{month.total_sales}</span>
                    {month.is_current && (
                      <span className="px-2 py-0.5 text-[10px] font-bold bg-emerald-100 text-emerald-700 rounded-full">NOW</span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Selected Month Details */}
      {selectedMonth && (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-lg text-slate-900">{selectedMonth.month} Details</h3>
            <span className="text-sm text-slate-500">{selectedMonth.period}</span>
            {loadingDetails && <RefreshCw className="w-4 h-4 text-indigo-600 animate-spin" />}
          </div>

          {monthDetails && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Daily Sales Chart */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="p-5 border-b border-slate-200">
                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                    <CalendarDays className="w-5 h-5 text-indigo-600" />Date-wise Sales
                  </h4>
                  <p className="text-xs text-slate-500 mt-1">Daily breakdown (Mon-Fri only)</p>
                </div>
                <div className="p-5">
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthDetails.daily_data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis dataKey="date" stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                        <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="sales" name="Sales" fill={showTeamView ? '#10b981' : '#6366f1'} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Weekly Sales Chart */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="p-5 border-b border-slate-200">
                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-600" />Week-wise Sales
                  </h4>
                  <p className="text-xs text-slate-500 mt-1">Weekly breakdown within month</p>
                </div>
                <div className="p-5">
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthDetails.weekly_data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis dataKey="week" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                        <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="sales" name="Sales" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Offer Breakdown */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="p-5 border-b border-slate-200">
                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Package className="w-5 h-5 text-amber-600" />Offer-wise Sales
                  </h4>
                </div>
                <div className="p-5">
                  {monthDetails.offer_data?.length > 0 ? (
                    <div className="flex gap-4">
                      <div className="w-1/2 h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={monthDetails.offer_data.slice(0, 6)}
                              cx="50%"
                              cy="50%"
                              innerRadius={40}
                              outerRadius={70}
                              paddingAngle={2}
                              dataKey="count"
                              nameKey="name"
                            >
                              {monthDetails.offer_data.slice(0, 6).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="w-1/2 space-y-2">
                        {monthDetails.offer_data.slice(0, 6).map((offer, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                              <span className="text-xs text-slate-700 truncate max-w-[100px]" title={offer.name}>{offer.name}</span>
                            </div>
                            <span className="text-sm font-bold text-slate-900">{offer.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-8">No offer data</p>
                  )}
                </div>
              </div>

              {/* User-wise Sales (Team View Only) */}
              {showTeamView && monthDetails.user_data && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="p-5 border-b border-slate-200">
                    <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Users className="w-5 h-5 text-emerald-600" />User-wise Sales
                    </h4>
                  </div>
                  <div className="p-3 max-h-[300px] overflow-y-auto">
                    {monthDetails.user_data.map((userData, i) => (
                      <div key={userData.id} className="mb-2">
                        <button
                          onClick={() => setExpandedUser(expandedUser === userData.id ? null : userData.id)}
                          className="w-full p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {i === 0 && <Crown className="w-5 h-5 text-amber-500" />}
                              {i === 1 && <Award className="w-5 h-5 text-slate-400" />}
                              {i === 2 && <Star className="w-5 h-5 text-orange-400" />}
                              {i > 2 && <span className="w-5 h-5 flex items-center justify-center text-xs font-bold text-slate-400">#{i + 1}</span>}
                              <span className="font-medium text-slate-900">{userData.name}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-lg font-bold text-slate-900">{userData.sales}</span>
                              {expandedUser === userData.id ? (
                                <ChevronUp className="w-4 h-4 text-slate-400" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-slate-400" />
                              )}
                            </div>
                          </div>
                        </button>
                        
                        {expandedUser === userData.id && userData.offers?.length > 0 && (
                          <div className="mt-2 ml-8 p-3 bg-slate-50 rounded-lg border border-slate-100">
                            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Offer Breakdown</p>
                            <div className="space-y-1">
                              {userData.offers.map((offer, j) => (
                                <div key={j} className="flex items-center justify-between text-sm">
                                  <span className="text-slate-700 truncate max-w-[150px]">{offer.name}</span>
                                  <span className="font-semibold text-slate-900">{offer.count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Summary Stats Cards */}
      <div>
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-indigo-600" />Monthly Overview
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {monthly_data.slice().reverse().slice(0, 6).map((month, index) => {
            // Create unique display label using month_num to avoid duplicate names
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const displayMonth = monthNames[(month.month_num || 1) - 1];
            const displayYear = month.year ? String(month.year).slice(-2) : '';
            
            return (
            <div
              key={`${month.year}-${month.month_num}-${index}`}
              onClick={() => setSelectedMonth(month)}
              className={`cursor-pointer p-4 rounded-xl border transition-all ${
                selectedMonth?.month === month.month && selectedMonth?.year === month.year
                  ? showTeamView 
                    ? 'bg-emerald-50 border-emerald-200 ring-2 ring-emerald-500/20'
                    : 'bg-indigo-50 border-indigo-200 ring-2 ring-indigo-500/20'
                  : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">
                  {displayMonth}{displayYear ? ` '${displayYear}` : ''}
                </span>
                {month.is_current && (
                  <span className="px-1.5 py-0.5 text-[9px] font-bold bg-emerald-100 text-emerald-700 rounded">NOW</span>
                )}
              </div>
              <p className="text-2xl font-bold text-slate-900">{month.total_sales}</p>
              <div className={`flex items-center gap-1 text-xs mt-1 ${
                month.growth_percentage >= 0 ? 'text-emerald-600' : 'text-red-600'
              }`}>
                {month.growth_percentage >= 0 ? (
                  <TrendingUp className="w-3 h-3" />
                ) : (
                  <TrendingDown className="w-3 h-3" />
                )}
                {Math.abs(month.growth_percentage)}%
              </div>
            </div>
          )})}
        </div>
      </div>
    </div>
  );
};

export default PerformancePage;
